import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tooltip } from 'antd'
import { InfoOutlined } from '@ant-design/icons'

import {
	actLoadMonthlyReports,
	actUndoOrDeleteReport
} from '../../../stores/ui-lst-monthly-reports/actions'
import { useStores } from '../../../cls/stores/MobXProvider'

import {
	StatusTag,
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	CustomTable
} from '../../../components/Shared'
import ProgressModal from '../../../components/Form/ProgressModal'
import { runInAction } from 'mobx'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import dayjs from 'dayjs'
import {
	newMonthlyReport,
	requireMonthlyReportsById,
	actSaveMonthlyReport
} from '../../../stores/monthly-reports/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'

/*
 * report's can be multiple types
 */
/**
 * @function LstItem
 * @description Renders a single row in the monthly reports list table. Displays status, metadata, and user-specific action controls based on permissions.
 * @param {string} id - The ID of the monthly report.
 * @param {boolean} isFirst - Indicates if the item is the first in the list (used for tour targeting).
 */
const LstItem = observer(({ id, isFirst }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { uiLstMonthlyReports, users } = useStores()
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const report = requireMonthlyReportsById(id)

	const selfPermission =
		report.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin

	// Delete is active only for the creator of the report if is in draft mode or if the user is an admin
	const deleteIsDisabled = !isAdmin
		? !(
				report.progress === 'draft' &&
				(loggedInUser.user_id === report.createdBy ||
					loggedInUser.user_id === report.user_id)
		  )
		: false

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	/**
	 * @function confirmUndoOrDelete
	 * @description Handles the confirmation action for deleting or undoing the deletion of a report.
	 */
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!report.to_delete) {
			actUndoOrDeleteReport(id, false).catch(console.error)
		} else {
			actUndoOrDeleteReport(id, true).catch(console.error)
		}
	}
	const _actions = [
		{
			key: 'status',
			label: 'Set Progress',
			value: 'status',
			disabled:
				!selfPermission && !users.canUser('write', 'monthly_reports')
		},
		{
			key: 'view',
			label: 'View',
			value: 'view',
			disabled:
				!selfPermission && !users.canUser('read', 'monthly_reports')
		},
		{
			key: 'data',
			label: 'Edit',
			value: 'data',
			disabled:
				(!!report.to_delete || !selfPermission) &&
				!users.canUser('write', 'monthly_reports')
		}
	]

	// Delete / Undo actions
	if (report.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin) {
		if (report.to_delete) {
			_actions.push({
				key: 'undo-del',
				label: 'Undo Delete',
				value: 'undo-delete',
				disabled:
					deleteIsDisabled && !users.canUser('delete', 'requests')
			})
		} else {
			_actions.push({
				key: 'del',
				label: 'Delete',
				value: 'delete',
				disabled:
					deleteIsDisabled && !users.canUser('delete', 'requests')
			})
		}
	}

	/**
	 * @function handleActions
	 * @description Handles dropdown action selection like view, edit, set progress, or delete.
	 * @param {string} value - The selected action.
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			case 'status':
				runInAction(() => {
					uiLstMonthlyReports.setModal('monthly-report-progress', {
						visible: true,
						itmId: id
					})
				})
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<CustomTable
					data={[
						[
							{
								item: (
									<Space style={{ width: '100%' }}>
										<StatusTag
											isShort
											id={
												isFirst
													? 'target-itm-progress'
													: ''
											}
											tag={report.progress}
											style={{
												marginRight: 0,
												padding: '0 3px'
											}}
										/>
										<Tooltip title="View item">
											<Link
												to={`${id}/view`}
												id={
													isFirst
														? 'target-itm-link'
														: ''
												}
											>
												{report.displayName}
											</Link>
										</Tooltip>
									</Space>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<CustomTable
					data={[
						[
							{
								item: (
									<Space
										style={{ width: '100%' }}
										id={isFirst ? 'target-itm-hours' : ''}
									>
										{report.hoursWorked}
									</Space>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<CustomTable
					data={[
						[
							{
								item: (
									<Space
										style={{ width: '100%' }}
										id={isFirst ? 'target-itm-user' : ''}
									>
										<Tooltip title="Created By">
											<UserString
												userId={report.createdBy}
												path="name"
											/>
										</Tooltip>
									</Space>
								)
							}
						]
					]}
				/>
			</td>

			<td>
				<Space style={{ width: '100%' }} direction="vertical">
					<Space>
						<Popover
							content={() => (
								<CustomTable
									data={[
										[
											{
												item: (
													<strong>
														{t('single.info')}
													</strong>
												)
											}
										],
										[
											t('single.updated-last'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															report.modifiedTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											'Created at:',
											{
												item: (
													<FormatedTimestamp
														timestamp={
															report.createdTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-by.label'),
											{
												item: (
													<UserString
														userId={
															report.createdBy
														}
														path="name"
													/>
												)
											}
										]
									]}
								/>
							)}
							trigger="hover"
						>
							<Button
								id={isFirst ? 'target-itm-info' : ''}
								color="default"
								icon={<InfoOutlined />}
							/>
						</Popover>
						<Select
							id={isFirst ? 'target-itm-actions' : ''}
							value={t('single.actions')}
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
						/>
					</Space>
					{
						// Check if the user has permission to view delete information
						// (either through selfPermission or a specific permission)
						// and if the report has a timestamp for deletion
						(selfPermission ||
							users.canUser('delete_view', 'monthly_reports')) &&
							report.toDeleteTs && (
								<>
									<span>
										{t('single.delete-on')}
										<FormatedTimestamp
											timestamp={report.toDeleteTs}
										/>
									</span>
								</>
							)
					}
				</Space>

				<Popconfirm
					title={
						report.to_delete
							? t('single.confirm-undo-delete')
							: t('single.confirm-delete')
					}
					open={showConfirmUndoOrDelete}
					onConfirm={confirmUndoOrDelete}
					onCancel={() => setConfirmUndoOrDelete(false)}
					okType="link"
					placement="topLeft"
				/>
			</td>
		</tr>
	)
})

LstItem.propTypes = {
	id: PropTypes.string.isRequired,
	isFirst: PropTypes.bool
}

/**
 * @function MonthlyReportsLst
 * @description Main component rendering the monthly reports table, filter controls, sync toggle, and tour integration.
 */
const MonthlyReportsLst = observer(() => {
	const navigate = useNavigate()
	const { t } = useSmartTranslation()
	const { uiLstMonthlyReports, tourStore, hasIntervalLoad, appType, users } =
		useStores()
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const bySearch = uiLstMonthlyReports.search
	const byDel = uiLstMonthlyReports.del
	const byCategory = uiLstMonthlyReports.filter.category
	const byProgress = uiLstMonthlyReports.filter.progress
	const user_uid = uiLstMonthlyReports.filter.user_uid

	const intervalIdRef = useRef(null) // Use ref for intervalId
	const [errorKey] = useState(null)
	const withIntervalLoad = hasIntervalLoad('monthly-reports')

	/**
	 * @function useEffect
	 * @description Handles the loading of reports on component mount and when filters or interval state change. Also manages auto-refresh logic based on interval setting.
	 */
	useEffect(() => {
		actLoadMonthlyReports().catch(console.error)
		// Function to cancel interval
		const cancelCheckAndLoad = () => {
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current)
			}
		}
		// Function to check if any item in list is modified and reload items it if necessary
		const checkAndLoadItems = () => {
			if (withIntervalLoad) {
				if (errorKey !== 'not_found') {
					actLoadMonthlyReports(true)
						.then(
							(res) =>
								res &&
								actLoadMonthlyReports().catch(console.error)
						)
						.catch(console.error)
				} else if (errorKey === 'not_found') {
					cancelCheckAndLoad()
				}
			} else {
				cancelCheckAndLoad()
			}
		}

		// Polling interval to check for modifications every 5 seconds
		if (withIntervalLoad) {
			intervalIdRef.current = setInterval(checkAndLoadItems, 5000)
		} else {
			cancelCheckAndLoad()
		}

		// Clean up interval on component unmount
		return () => cancelCheckAndLoad()
	}, [
		bySearch,
		byDel,
		byCategory,
		byProgress,
		user_uid,
		withIntervalLoad,
		appType.appIs
	])

	// generate tour item that will be hidden based on the permissions
	const hideTourItem = []
	const hideFilters = []
	if (
		!(
			isAdmin ||
			isSuperAdmin ||
			users.canUser('delete_view', 'monthly_reports')
		)
	) {
		hideFilters.push('deleted')
		hideTourItem.push('delete')
	}

	if (
		!(isAdmin || isSuperAdmin || users.canUser('write', 'monthly_reports'))
	) {
		hideFilters.push('addNew')
		hideTourItem.push('new')
	}

	const tourMonthlyReportLst = getTourArrayByKeys('monthlyReportLst', ['all'])
	const tourFilter = getTourArrayByKeys(
		'filter',
		[
			'section',
			'search',
			'progress',
			'priority',
			'new',
			'toggleBtn',
			'ByUser',
			'orderBy',
			'delete'
		],
		hideTourItem
	)

	/**
	 * Start tour:
	 * This function starts the tour for the sprint form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		runInAction(() => {
			// Open advance filter when we start tour
			uiLstMonthlyReports.filterExpanded = true
		})

		const hasItems = uiLstMonthlyReports.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() => {
				actUndoOrDeleteReport(newId, true, true).catch(console.error)
			})

			const demoObj = {
				createdTs: dayjs().unix(),
				createdBy: loggedInUserId,
				for_user_id: loggedInUserId,
				name: dayjs().unix(),
				progress: 'draft',
				subtasks: [],
				comments: []
			}

			runInAction(() => {
				actSaveMonthlyReport(newId, demoObj, true).catch(console.error)
			})
		}
		tourStore.startTour('MonthlyReportLst-tour', [
			{
				title: 'Sync on interval',
				description:
					'Enable or disable data synchronization on interval for the sprints list. Any additions or modifications will be automatically updated even if working in multiple tabs or list/items are updated by multiple users.',
				target: () => document.getElementById('target-interval-lst')
			},
			...tourFilter,
			...tourMonthlyReportLst
		])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_monthly-report.multiple') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			></GeneralTourTitle>
			<Filter
				uiStore={uiLstMonthlyReports}
				hide={[...hideFilters, 'category', 'for-user']}
				addNew={() => {
					const newId = uuidv4()
					newMonthlyReport(newId).then(() => {
						navigate(`/monthly-reports/${newId}/edit`)
					})
				}}
			/>

			<ProgressModal
				modalKey="monthly-report-progress"
				uiStore={uiLstMonthlyReports}
				requireItemById={requireMonthlyReportsById}
				loadItm={(id) => actLoadMonthlyReports(id)}
				saveItem={actSaveMonthlyReport}
				type="monthly-report"
			/>

			<LstTable
				LstItem={LstItem}
				store={uiLstMonthlyReports}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-monthly-report">{t('single.month')}</th>
					<th id="target-hours">{t('_hour.multiple')}</th>
					<th id="target-user">{t('_user.multiple')}</th>
					<th id="target-actions">{t('single.actions')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})

export default MonthlyReportsLst
